/**
 * BusyButton (Task 9) — the shared "no duplicate presses" button. Wraps the raw
 * `btn` idiom: give it an async `onAction` and, from the moment it's invoked
 * until the returned promise settles, the button is `disabled` + `aria-busy` and
 * a second click is swallowed. This folds two existing hand-rolled idioms into
 * one reusable piece: BatchPanel's `disabled + aria-busy`, and Wizard.tsx's
 * `generatingRef` double-click guard (the ref latches BEFORE React re-renders,
 * so the very next synchronous click is ignored even mid-paint).
 *
 * The only new prop is `onAction`; everything else passes straight to the native
 * <button>, so each adopted call site keeps its own classes, testids, labels and
 * any extra `disabled` condition (composed with `||`). A synchronous (void)
 * handler never enters the busy state. `type` defaults to "button" so a button
 * dropped inside a <form> never accidentally submits it.
 */
import { useEffect, useRef, useState } from "react";

export type BusyButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick"
> & {
  /** Runs on click; the button stays busy from invocation until this settles. */
  onAction: () => Promise<unknown> | void;
  /** What to show while busy. Defaults to the children plus a volt spinner. */
  busyLabel?: React.ReactNode;
};

export function BusyButton({ onAction, busyLabel, children, disabled, type, ...rest }: BusyButtonProps) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // ref-guard: a 2nd click is ignored before the re-render lands
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const click = () => {
    if (inFlight.current) return;
    const r = onAction();
    // Only enter the busy state for a real thenable — a sync/void handler stays idle.
    if (r && typeof (r as Promise<unknown>).finally === "function") {
      inFlight.current = true;
      setBusy(true);
      (r as Promise<unknown>)
        .finally(() => {
          inFlight.current = false;
          if (alive.current) setBusy(false);
        })
        // The button owns only the busy lifecycle; a rejecting handler is that
        // handler's concern (the adopted call sites that need error UI catch
        // internally). Consume our derived chain so we never leak an unhandled
        // rejection from the button's own bookkeeping.
        .catch(() => {});
    }
  };

  return (
    <button
      {...rest}
      type={type ?? "button"}
      disabled={disabled || busy}
      aria-busy={busy}
      onClick={click}
    >
      {busy
        ? busyLabel ?? (
            <>
              {children}
              <span className="btn-spinner" aria-hidden="true" />
            </>
          )
        : children}
    </button>
  );
}
